import React, { useState } from 'react';
import { 
  User, 
  Shield, 
  Download, 
  Trash2, 
  CreditCard,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Mail,
  Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useLifely } from '../context/LifelyContext';
import { accountAPI } from '../services/api';
import { toast } from 'sonner';
import { APP_VERSION } from '../config/version';

const Settings = () => {
  const { user } = useLifely();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Get display name from user object
  const displayName = user?.username || user?.firstName || user?.email?.split('@')[0] || 'User';
  const displayEmail = user?.email || '';
  const displayInitial = displayName.charAt(0).toUpperCase();

  const handleDeleteRequest = async () => {
    try {
      setIsDeleting(true);
      await accountAPI.requestDeletion();
      setEmailSent(true);
      setDeleteStep(3);
      toast.success('Confirmation email sent. Please check your inbox.');
    } catch (err) {
      toast.error('Failed to send confirmation email. Please try again.');
      console.error('Delete request error:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const resetDeleteDialog = () => {
    setShowDeleteDialog(false);
    setDeleteStep(1);
    setEmailSent(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <section className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground">
          Settings
        </h1>
        <p className="text-muted-foreground text-base">
          Manage your account, privacy preferences, and data.
        </p>
      </section>

      {/* Profile Section */}
      <Card className="border border-border" data-testid="profile-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
            <User className="w-4 h-4 text-primary" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-2xl font-heading font-semibold text-primary">
                {displayInitial}
              </span>
            </div>
            <div>
              <p className="font-medium text-lg">{displayName}</p>
              <p className="text-sm text-muted-foreground">{displayEmail}</p>
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Account created</p>
                <p className="text-sm text-muted-foreground">{user?.createdAt || '—'}</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Last login</p>
                <p className="text-sm text-muted-foreground">{user?.lastLogin || '—'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Privacy & Data Section */}
      <Card className="border border-border" data-testid="privacy-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
            <Shield className="w-4 h-4 text-primary" />
            Privacy & Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium">Export your data</p>
              <p className="text-xs text-muted-foreground">
                Download a copy of all your Lifely data
              </p>
            </div>
            <Button variant="outline" size="sm" disabled data-testid="export-data-btn">
              <Download className="w-4 h-4 mr-1" />
              Export
              <span className="ml-1 text-xs">(Soon)</span>
            </Button>
          </div>

          <Separator />

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-destructive">Delete your account</p>
              <p className="text-xs text-muted-foreground">
                Permanently delete your account and all data
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-destructive border-destructive/30 hover:bg-destructive/10" 
              data-testid="delete-account-btn"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Billing Section */}
      <Card className="border border-border" data-testid="billing-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
            <CreditCard className="w-4 h-4 text-primary" />
            Billing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <p className="font-medium text-sm">Current Plan</p>
            <p className="text-2xl font-heading font-semibold mt-1">Free</p>
            <p className="text-xs text-muted-foreground mt-1">
              Basic access to all maps and continuity features
            </p>
          </div>

          <Button variant="outline" disabled className="w-full" data-testid="upgrade-plan-btn">
            Upgrade Plan
            <span className="ml-2 text-xs">(Coming soon)</span>
          </Button>
        </CardContent>
      </Card>

      {/* Sign Out */}
      <div className="pt-4">
        <Button 
          variant="ghost" 
          className="w-full text-muted-foreground hover:text-foreground"
          data-testid="sign-out-btn"
        >
          Sign Out
        </Button>
      </div>

      {/* Version Info */}
      <div className="pt-4 pb-8 text-center">
        <p className="text-xs text-muted-foreground">
          Lifely v{APP_VERSION}
        </p>
      </div>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={resetDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Account
            </DialogTitle>
          </DialogHeader>

          {deleteStep === 1 && (
            <>
              <DialogDescription className="space-y-4 pt-2">
                <p>
                  Are you sure you want to delete your Lifely account? This action is <strong>permanent and cannot be undone</strong>.
                </p>
                
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
                  <p className="font-medium text-sm text-destructive">This will permanently delete:</p>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Your Lifely account and profile</li>
                    <li>• All maps and entries (assets, properties, digital accounts)</li>
                    <li>• All contacts and sharing settings</li>
                    <li>• Will status and formal arrangements</li>
                    <li>• Continuity settings and nominees</li>
                    <li>• Any active subscription</li>
                  </ul>
                </div>
              </DialogDescription>

              <DialogFooter className="gap-2 sm:gap-0 mt-4">
                <Button variant="outline" onClick={resetDeleteDialog}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => setDeleteStep(2)}
                >
                  I Understand, Continue
                </Button>
              </DialogFooter>
            </>
          )}

          {deleteStep === 2 && (
            <>
              <DialogDescription className="space-y-4 pt-2">
                <p>
                  To confirm deletion, we&apos;ll send a verification email to:
                </p>
                
                <div className="bg-muted rounded-lg p-3 flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium">{displayEmail}</span>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Click the link in the email to permanently delete your account. The link will expire in 24 hours.
                </p>
              </DialogDescription>

              <DialogFooter className="gap-2 sm:gap-0 mt-4">
                <Button variant="outline" onClick={() => setDeleteStep(1)}>
                  Back
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleDeleteRequest}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Confirmation Email'
                  )}
                </Button>
              </DialogFooter>
            </>
          )}

          {deleteStep === 3 && (
            <>
              <DialogDescription className="space-y-4 pt-2">
                <div className="text-center py-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="font-medium text-lg mb-2">Check Your Email</p>
                  <p className="text-muted-foreground">
                    We&apos;ve sent a confirmation link to <strong>{displayEmail}</strong>
                  </p>
                </div>
                
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Important:</strong> Your account will only be deleted after you click the confirmation link in the email. The link expires in 24 hours.
                  </p>
                </div>
              </DialogDescription>

              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={resetDeleteDialog} className="w-full">
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
